// =============================================================================
// src/features/explore/explore-globals.ts — Acesso tipado a globais de coexistencia.
//
// Proveniencia: assets/explore.js le `window.SearchPalette`/`TopbarNav`/
//   `MapController`/`ExploreRoute` (linhas 507, 545-550, 671-686) para integrar
//   com o resto do app. Durante a coexistencia TS<->legado, alguns desses ainda
//   podem vir do runtime legado; encapsulamos a leitura aqui com tipos estreitos
//   (zero any) e acessores que devolvem o modulo ou undefined.
//
// Quando os ports TS de TopbarNav/SearchPalette/MapController estiverem ligados em
//   main.ts via AppContext, estes acessores podem ser substituidos por injecao —
//   por ora preservam o comportamento exato do legado sem reintroduzir `var DATA`.
// =============================================================================

import type { MapController, MunicipalityId, ZonaKey } from '../../app/types';

/** Command palette global (assets/unified-app.js SearchPalette). */
interface SearchPaletteGlobal {
  open(): void;
}

/** Navegacao da topbar (assets/unified-app.js TopbarNav). */
interface TopbarNavGlobal {
  go(tab: string): void;
  syncActive(): void;
}

/** Superficie do MapController consumida pelo drill-in do card. */
interface MapControllerDrillIn {
  focusZone?(zona: ZonaKey): void;
  focusMun?(munId: MunicipalityId): void;
  invalidateSize?(): void;
}

/** Rota Explorar (auto-referencia para fechar/checar estado). */
interface ExploreRouteGlobal {
  isOpen(): boolean;
  close(): void;
}

/** Rota fullscreen irma (Grafo/Workspace) — fechada ao abrir a Explorar. */
interface SiblingRouteGlobal {
  isOpen?(): boolean;
  close(): void;
}

/** Globais de coexistencia legidos pela feature (todos opcionais). */
interface ExploreCoexistenceGlobals {
  SearchPalette?: SearchPaletteGlobal;
  TopbarNav?: TopbarNavGlobal;
  MapController?: MapController & MapControllerDrillIn;
  ExploreRoute?: ExploreRouteGlobal;
  GrafoRoute?: SiblingRouteGlobal;
  WorkspaceRoute?: SiblingRouteGlobal;
}

/** Janela estreitada aos globais de coexistencia (sem poluir o tipo Window global). */
function coexistenceWindow(): ExploreCoexistenceGlobals {
  return window as unknown as ExploreCoexistenceGlobals;
}

/** Devolve a command palette global se presente. */
export function getSearchPalette(): SearchPaletteGlobal | undefined {
  return coexistenceWindow().SearchPalette;
}

/** Devolve a navegacao da topbar se presente. */
export function getTopbarNav(): TopbarNavGlobal | undefined {
  return coexistenceWindow().TopbarNav;
}

/** Devolve o MapController (drill-in) se presente. */
export function getMapController(): (MapController & MapControllerDrillIn) | undefined {
  return coexistenceWindow().MapController;
}

/** Devolve a rota Explorar global se presente. */
export function getExploreRouteGlobal(): ExploreRouteGlobal | undefined {
  return coexistenceWindow().ExploreRoute;
}

/** Devolve a rota Grafo global se presente. */
export function getGrafoRoute(): SiblingRouteGlobal | undefined {
  return coexistenceWindow().GrafoRoute;
}

/** Devolve a rota Workspace global se presente. */
export function getWorkspaceRoute(): SiblingRouteGlobal | undefined {
  return coexistenceWindow().WorkspaceRoute;
}
